import { environment } from "../../config/environment";
import React, { useState, useEffect } from "react";
import { customFetch } from "../../utils/Auth";

const LLMSelection = () => {
  const [llmModels, setLlmModels] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  // Mapping for user-friendly function names
  const functionNameMapping = {
    'RunEvaluationManagementFunction': 'E&M Lambda',
    'RunICD10CodingV2Function': 'ICD-10 Lambda - Commercial',
    'RunICD10CodingVAFunction': 'ICD-10 Lambda - VA',
    'RunOrdersV2Function': 'Orders Lambda',
    'RunPatientSummaryFunction': 'Patient Summary Lambda',
    'RunChatgptFunction': 'RunChatGPTSummarize Lambda',
    'RunMacrosFunction': 'Macros Lambda'
  };

  // Mapping for model display names
  const modelDisplayMapping = {
    'anthropic:claude-sonnet-4-6': 'Claude Sonnet 4.6',
    'anthropic:claude-sonnet-4-5-20250929': 'Claude Sonnet 4.5',
    'anthropic:claude-3-7-sonnet-20250219': 'Claude 3.7 Sonnet',
    'anthropic:claude-3-5-sonnet-20241022': 'Claude 3.5 Sonnet',
    'openai:gpt-4o': 'GPT 4o',
    'openai:gpt-5': 'GPT 5',
    'knowtex:mock': 'Knowtex Mock'
  };

  // Available model options
  const modelOptions = [
    'anthropic:claude-sonnet-4-6',
    'anthropic:claude-sonnet-4-5-20250929',
    'anthropic:claude-3-7-sonnet-20250219',
    'anthropic:claude-3-5-sonnet-20241022',
    'openai:gpt-4o',
    'openai:gpt-5',
    'knowtex:mock'
  ];

  // Fetch all LLM model parameters
  const fetchLLMModels = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await customFetch(`${environment.llmApiUrl}/llm/models`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setLlmModels(data.models || []);
        } else {
          throw new Error(data.error || 'Failed to fetch LLM models');
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch LLM models');
      }
    } catch (err) {
      console.error('Error fetching LLM models:', err);
      setError(`Failed to load LLM models: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Update a specific LLM model
  const handleModelUpdate = async (functionName, newValue) => {
    setIsLoading(true);
    setError(null);
    setSuccessMessage("");
    
    try {
      const response = await customFetch(`${environment.llmApiUrl}/llm/models/${functionName}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ modelValue: newValue }),
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const displayName = functionNameMapping[functionName] || functionName;
          const modelDisplayName = modelDisplayMapping[newValue] || newValue;
          setSuccessMessage(`Successfully updated ${displayName} to: ${modelDisplayName}`);
          // Refresh the models list
          await fetchLLMModels();
        } else {
          throw new Error(data.error || 'Failed to update model');
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update model');
      }
    } catch (err) {
      console.error('Error updating model:', err);
      const displayName = functionNameMapping[functionName] || functionName;
      setError(`Failed to update ${displayName}. ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle dropdown change
  const handleDropdownChange = (functionName, newValue) => {
    if (newValue && newValue !== '') {
      handleModelUpdate(functionName, newValue);
    }
  };

  useEffect(() => {
    fetchLLMModels();
  }, []);

  return (
    <div className="w-full p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800">
          LLM Model Configuration
        </h2>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="text-red-400">⚠</div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <div className="text-green-400">✓</div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">Success</h3>
              <div className="mt-2 text-sm text-green-700">{successMessage}</div>
            </div>
          </div>
        </div>
      )}

      {/* LLM Models Table */}
      <div className="space-y-4">
        
        {isLoading ? (
          <div className="text-center py-4">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-sm text-gray-600">Loading models...</p>
          </div>
        ) : llmModels.length > 0 ? (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {llmModels.map((model) => (
                <li key={model.functionName} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 truncate">
                            {functionNameMapping[model.functionName] || model.functionName}
                          </h4>
                        </div>
                        <div className="flex items-center space-x-2">
                          <select
                            value={model.currentValue}
                            onChange={(e) => handleDropdownChange(model.functionName, e.target.value)}
                            disabled={isLoading}
                            className="min-w-[200px] border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:border-ktx-purple disabled:bg-gray-100 disabled:cursor-not-allowed"
                          >
                            <option value="">Select a model...</option>
                            {modelOptions.map((option) => (
                              <option key={option} value={option}>
                                {modelDisplayMapping[option] || option}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">No LLM models found</p>
          </div>
        )}
      </div>

    </div>
  );
};

export default LLMSelection;
